'use client';

import { Box, Typography } from '@/primitives';

const sectionStyle = { mb: 2 };
const headingStyle = { fontWeight: 'bold', mb: 1, mt: 3 };
const subHeadingStyle = { fontWeight: 'bold', mb: 1, mt: 2 };
const listStyle = {
  pl: 3,
  mb: 1,
  '& li': { mb: 0.5 },
  listStyleType: 'lower-alpha',
};

function DefinitionRow({
  term,
  definition,
}: {
  term: string;
  definition: string;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
      <Typography
        variant="bodysmall"
        sx={{ fontWeight: 'bold', minWidth: 180, flexShrink: 0 }}
      >
        {term}
      </Typography>
      <Typography variant="bodysmall">{definition}</Typography>
    </Box>
  );
}

interface EulaContentProps {
  /** When true, applies page-level max-width + outer padding (use on standalone routes). */
  page?: boolean;
}

export function EulaContent({ page = false }: EulaContentProps = {}) {
  return (
    <Box
      sx={{
        color: 'text.primary',
        ...(page
          ? {
              width: '100%',
              maxWidth: { xs: '100%', md: 1000 },
              mx: 'auto',
              px: { xs: 2, md: 4 },
              py: { xs: 2, md: 4 },
            }
          : { px: 2, py: 1 }),
      }}
    >
      <Typography variant="headlinesmall" sx={{ mb: 2, fontWeight: 'bold' }}>
        End User Licence Agreement - Standard Terms and Conditions
      </Typography>

      <Box sx={sectionStyle}>
        <Typography variant="bodysmall">
          <strong>Between:</strong> Agricultural Business Research Institute ABN
          59 781 301 088 of Short Run, University of New England, ARMIDALE NSW
          2351 (Licensor);
        </Typography>
        <Typography variant="bodysmall" sx={{ mt: 1 }}>
          <strong>And:</strong> &quot;You&quot;. In this document You and Your
          means the natural person(s) or legal entity which has entered into a
          licence to use any Software and Materials owned by the Licensor.
        </Typography>
      </Box>

      <Typography variant="titlemedium" sx={headingStyle}>
        Introduction
      </Typography>
      <Box component="ol" sx={{ ...listStyle, listStyleType: 'upper-alpha' }}>
        <li>
          <Typography variant="bodysmall">
            The Licensor is the owner of, and has the right to license, the
            Software and the Materials.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            You wish to use the Software and the Materials.
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            The Licensor has agreed to license the Software and the Materials to
            You.
          </Typography>
        </li>
      </Box>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        What follows are the standard terms and conditions that apply when the
        Licensor licences the Software and Materials to You. By using the
        Software that the Licensor licences to You, You acknowledge that You
        have read and understood these Terms and Conditions and you accept and
        agree to be bound by these Terms and Conditions. If You do not agree
        with any of the Terms and Conditions You may not use the Licensor&apos;s
        Software.
      </Typography>

      {/* 1. Definitions */}
      <Typography variant="titlemedium" sx={headingStyle}>
        1. Definitions
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 1 }}>
        In these Terms and Conditions the terms referred to in Column 1 when
        used with capital letters shall have the meanings ascribed to them in
        Column 2 below unless their context otherwise requires:
      </Typography>
      <Box sx={{ mb: 2 }}>
        <DefinitionRow
          term="Account"
          definition="a registered account and login to access the Software"
        />
        <DefinitionRow
          term="Copyright"
          definition="those rights of copyright under the Commonwealth Copyright Act in respect of the Software and the Materials and other rights of copyright throughout the World."
        />
        <DefinitionRow
          term="Data Error"
          definition="means an error in the Livestock Data flowing from an act or omission of a party other than the Licensor, including without limitation: (ii) incorrect entry of Livestock Data; (iii) errors in Livestock Data transcription or manipulation; and (iv) incomplete or misleading Livestock Data."
        />
        <DefinitionRow
          term="BREEDPLAN EBV"
          definition="means Estimated Breeding Values displayed within the Software."
        />
        <DefinitionRow
          term="BREEDPLAN EPD"
          definition="means Estimated Progeny Difference displayed within the Software."
        />
        <DefinitionRow
          term="Future Rights"
          definition="jointly and severally all those rights (statutory and otherwise) comprised in the definition of Patent Rights, Trade Mark Rights or Copyright and which may be granted or acquired in any way whatsoever in relation to the Software and the Materials and which shall be developed by the Licensor or You in the future by or on behalf of the Licensor or You and in respect of all media either in existence now or in the future."
        />
        <DefinitionRow
          term="Improvement"
          definition="any method which can be adapted to the Software which could make its operation or conduct cheaper, more effective or in any way easier or more preferable to commercialise."
        />
        <DefinitionRow
          term="Intellectual Property Rights (IPR)"
          definition="jointly and severally the Future Rights, Patent Rights, Copyright, Trade Mark Rights and Technical Information."
        />
        <DefinitionRow
          term="Licence"
          definition="the licence of the Software granted to You in clause 2"
        />
        <DefinitionRow
          term="Licence Fee"
          definition="the fee payable by You to the Licensor to access the Software."
        />
        <DefinitionRow
          term="Livestock Breed Society"
          definition="a registered Breed Association or Breed Society responsible for the official register of a livestock breed."
        />
        <DefinitionRow
          term="Livestock Data"
          definition="the information, data and values provided by the Member or Livestock Breed Society for use in providing the Services, including, but not limited to, animal identifiers, recorded pedigrees, genomic profiles, records of performance, and records of non-genomic factors (e.g. date of birth, management group) but does not include Results."
        />
        <DefinitionRow
          term="Material"
          definition="domain names, trade marks, web-sites, graphs, models photographs, letters, client lists, formulae, notes, correspondence, drawings, manuals and other written material or software relating to or included in the Software."
        />
        <DefinitionRow
          term="Member User"
          definition="an individual User or entity User with an Account on the system which is linked to a Tenant, controlled by an entity, group, company or institution."
        />
        <DefinitionRow
          term="Patent Rights"
          definition="jointly and severally any registered patents and patent applications (if any) in respect of the Software and the right to register related patent applications throughout other countries of the World."
        />
        <DefinitionRow
          term="Purpose"
          definition="the purpose for which the Software may be used by the Licensee, including but not limited to: recording performance, pedigree and genotypic data on their animals; searching for and finding animals with specific traits, characteristics, and genetics for potential purchase, breeding, or research; viewing and analysing the results of genetic evaluations conducted on their animals; accessing data and managing animal registrations, members, and genetic evaluations by third parties such as breed societies; marketing and promoting livestock; supporting genetic improvement across cohorts of animals; accessing pedigree, performance and genotypic data to process in genetic evaluations; accessing data to display externally to support the sale of animals; accessing data to support the purchase of animals including additional analysis, comparison, and matching of animals based on genetics."
        />
        <DefinitionRow
          term="Results"
          definition="data or information created by the analysis, manipulation, processing or exploitation of the Livestock Data by the Software."
        />
        <DefinitionRow
          term="Software"
          definition="the software and other IPR owned by the Licensor and licenced to You."
        />
        <DefinitionRow
          term="Technical Information"
          definition="all information relating to the Software which is confidential to the Licensor prior to or during the subsistence of any licence with you."
        />
        <DefinitionRow
          term="Tenant"
          definition="A group of associated Users with common privileges, rules and access to functionality within the software."
        />
        <DefinitionRow
          term="Term"
          definition="the period of time which the Licensor has licensed the Software to You."
        />
        <DefinitionRow
          term="Trade Mark Rights"
          definition="those rights in registered trade marks and trade mark applications (if any) and such other names and symbols developed or acquired by the Licensor or You in the future or which might describe the Software or the operation of the Licensor and capable of protection under the Trade Marks Act (as amended) and the Trade Mark Regulations of the Commonwealth of Australia and by other laws throughout the World, and the Corporations Act (Cth) in relation to the provisions dealing with business names."
        />
        <DefinitionRow
          term="User(s)"
          definition="any person who accesses and uses the system, including persons who have a registered Account on the system and persons accessing the site without an account."
        />
      </Box>

      {/* 1. Interpretation */}
      <Typography variant="titlesmall" sx={subHeadingStyle}>
        1. Interpretation
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          1.1 References to legislation or provisions of legislation include
          changes or re-enactments of the legislation and statutory instruments
          and regulations issued under, the legislation.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          1.2 Words denoting the singular include the plural and vice versa;
          words denoting individuals or persons include bodies corporate and
          vice versa; references to documents or agreements also mean those
          documents or agreement as changed, novated or replaced, and words
          denoting one gender include all genders.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          1.3 References to a party are intended to bind their executors,
          administrators and permitted transferees.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          1.4 The Licence and these Terms and Conditions will be governed by the
          laws of New South Wales, Australia. You agree to submit to the
          non-exclusive jurisdiction of the courts of New South Wales in the
          event of a dispute arising out of, or in connection with the Licence
          or these Terms and Conditions.
        </Typography>
      </Box>

      {/* 2. Licence Fee */}
      <Typography variant="titlemedium" sx={headingStyle}>
        2. Licence Fee
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          2.1 Your Account may require you to pay a License Fee to the Licensor
          during the Term, on the terms associated with your Account type.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          2.2 The Licence Fee may be varied from time to time by the Licensor.
          The Licensor will notify you in writing of any variation in the
          Licence Fee at least 30 days before the variation takes effect.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          2.3 If You do not pay the Licence Fee as and when it falls due then
          the Licensor reserves the right to terminate the Licence in accordance
          with the terms of clause 13.
        </Typography>
      </Box>

      {/* 3. Your Obligations */}
      <Typography variant="titlemedium" sx={headingStyle}>
        3. Your Obligations and Undertakings
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
        3.1 Throughout the subsistence of the Licence, You undertake:
      </Typography>
      <Box component="ol" sx={listStyle}>
        <li>
          <Typography variant="bodysmall">
            to use Your best endeavours to preserve the value and validity of
            the Software and any IPR in the Software;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            not to allow or facilitate the use of the Software or the IPR in the
            Software in a manner or in any way detrimental to the operation of
            the Licensor;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            not to disassemble, de-compile, decrypt, or reverse engineer the
            Software or the IPR or assist any person to do so;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            not to reproduce, modify or adapt the Software or the IPR or create
            any derivative software based on the Software or the IPR;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            not to distribute or transfer the Software or IPR to any person;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            not to assign, sublicense, lease or sell the use of the Software or
            IPR to any person;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            not at any time to contravene, deny, contest, contribute nor object
            to in any way directly or indirectly in respect of the rights
            subsisting in the Software or the IPR in the Software;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            in connection with the exploitation of the Software, to refrain from
            giving a warranty in respect of the IPR in the Software beyond that
            which You are obliged in law to give or which has not been approved
            in writing by the Licensor;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            to treat as confidential the Software, Materials and Technical
            Information except that which at the time of its disclosure to You
            was generally available or subsequently became known to the public;
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            to disclose the Software and Materials only to Your employees,
            affiliates, agents and contractors who need to access such
            information so that You can exercise Your rights and obligations
            under this Licence; and
          </Typography>
        </li>
        <li>
          <Typography variant="bodysmall">
            to devote Your best endeavours to the conduct of the Licence.
          </Typography>
        </li>
      </Box>
      <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
        3.2 You acknowledge You are responsible for ensuring that Your hardware
        systems and software environment meet the minimum system requirements
        for the Software as amended and advised to You from time to time.
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        3.3 You agree that in using the Software the Licensor is not responsible
        for any data loss, equipment or other hardware damage or malfunctions
        You may suffer arising as a result of Your use of the Software or
        interaction of the Software with any third-party software.
      </Typography>

      {/* 4. Software and Materials Ownership */}
      <Typography variant="titlemedium" sx={headingStyle}>
        4. Software and Materials Ownership, Licence and Intellectual Property
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.1 You acknowledge and agree that the Licensor is the owner of all
          IPR in the Software and the Materials.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.2 In consideration of the payment by You to the Licensor of the
          Licence Fee the Licensor grants to You for the Term a personal,
          worldwide, non-exclusive, non-transferable Licence (without the right
          to sublicence) to use the Software.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.3 You accept the Licence granted to You in the clause above.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.4 You must not alter, remove or obscure any trade mark or copyright
          symbol or legend or other proprietary mark on the Software and the
          Materials.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.5 You are responsible for obtaining all approvals necessary to
          exercise Your rights under this Licence.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.6 You may access and use the Software on multiple devices.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.7 You do not have any rights to the source code or algorithms of the
          Software, nor to use, reproduce, modify or otherwise exercise the IPR
          subsisting in the Software or any database that the Software may
          create as a result of Your use of the Software.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          4.8 You acknowledge that:
        </Typography>
        <Box component="ol" sx={listStyle}>
          <li>
            <Typography variant="bodysmall">
              the Licensor may change the Software in minor or substantial and
              material ways in updates, patches, recalibrations or later
              versions. You acknowledge that such changes may modify the
              Software&apos;s functionality, but unless the changes render the
              Software unfit for the Purpose, You shall not be entitled to any
              refund of the Licence Fee;
            </Typography>
          </li>
          <li>
            <Typography variant="bodysmall">
              the Licensor may cease to offer the Software on the giving to the
              Licensee a minimum of 90 days&apos; notice;
            </Typography>
          </li>
          <li>
            <Typography variant="bodysmall">
              the Licensor may release software which is capable of fulfilling
              the Purpose which has different fees and/or functionality to the
              Software.
            </Typography>
          </li>
        </Box>
      </Box>

      {/* 5. Tenant */}
      <Typography variant="titlemedium" sx={headingStyle}>
        5. Tenant
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          5.1 You acknowledge that You may elect to link your Account to a
          Tenant, and that Your access to the Software and use of the Software
          may be governed by the membership rules, guidelines and specifications
          of that Tenant.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          5.2 You acknowledge that any data that You share with the Tenant may
          be subject to a license agreement with the entity that controls the
          Tenant.
        </Typography>
      </Box>

      {/* 6. Livestock Data */}
      <Typography variant="titlemedium" sx={headingStyle}>
        6. Livestock Data Ownership, Licence and Intellectual Property
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          6.1 The Licensor acknowledges that the Livestock Data and resultant
          intellectual property, whether collected by the User or Tenant,
          provided by You for use in the Software during the operation of the
          Licence is the property of the User.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          6.2 You grant to the Licensor a perpetual, non-exclusive,
          royalty-free, world-wide licence to use, display, reproduce, modify,
          aggregate and otherwise exercise IPRs in the Livestock Data for all
          purposes associated with operating the Licensor&apos;s entity.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          6.3 You warrant that you hold all necessary permissions to use and
          share the Livestock Data for use in the Software and to grant the
          license in clause 6.2. You agree to indemnify and keep the Licensor
          indemnified against any and all loss, costs and expenses arising out
          of Your breach of this clause.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          6.4 You may, at your own discretion grant licenses to third parties
          (e.g. breed societies, genetic evaluation providers, etc.).
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          6.5 You may grant permissions to other Users within the Software to
          view, manage, and alter Your Livestock Data and Personal Information.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          6.6 You acknowledge, in using the Software and any Results displayed
          within the Software under this licence, that the Licensor does not
          evaluate or examine the accuracy or completeness of any Livestock Data
          and does not guarantee the accuracy of any Results or outputs produced
          by the Software.
        </Typography>
      </Box>

      {/* 7. Personal Information */}
      <Typography variant="titlemedium" sx={headingStyle}>
        7. Collection and Use of Personal Information
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          7.1 You acknowledge that the Licensor may collect, store and use your
          Personal Information for marketing and/or sales purposes (unless you
          opt out) and to display on the Software (unless you opt out).
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          7.2 The Licensor may not distribute, transfer, share or use Your
          Personal Information in any way, without Your consent.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          7.3 You may request that the Licensor erases your Personal Information
          from the Software, except where this would violate a third-party
          license.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          7.4 You may request that the Licensor erases your Livestock Data from
          the Software, subject to third-party license obligations and
          operational requirements.
        </Typography>
      </Box>

      {/* 8. GDPR */}
      <Typography variant="titlemedium" sx={headingStyle}>
        8. GDPR Compliance
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.1 The Parties agree to comply with all applicable data protection
          laws, including the General Data Protection Regulation (EU) 2016/679
          (&quot;GDPR&quot;).
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.2 The Licensor is the data controller with respect to personal data
          collected in connection with the Software. You are the data controller
          with respect to personal data You upload and process using the
          Software.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.3 The Licensor shall process personal data only on documented
          instructions from You and shall ensure authorised persons have
          committed to confidentiality obligations.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.4 The Licensor warrants appropriate technical and organisational
          security measures including pseudonymisation, encryption, and incident
          recovery processes.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.5 The Licensor shall not engage another processor without Your prior
          authorisation and shall inform You of any intended changes.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.6 The Licensor shall assist You in responding to data subject rights
          requests under Chapter III of the GDPR.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.7 The Licensor shall notify You without undue delay of any personal
          data breach.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          8.8-8.11 The Licensor shall assist with data protection impact
          assessments, delete or return personal data upon request, make
          compliance information available, and You agree to indemnify the
          Licensor from breaches of this GDPR clause by You.
        </Typography>
      </Box>

      {/* 9. Indemnities */}
      <Typography variant="titlemedium" sx={headingStyle}>
        9. Indemnities and Warranties
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          9.1 You indemnify the Licensor against all claims, demands, losses,
          damages and costs arising from: (a) a breach by You of obligations
          under this Licence; (b) any act, default, omission, misconduct or
          negligence by You; (c) any third party claim arising from Your use of
          the Software.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          9.2 You acknowledge that You have not relied on any warranties other
          than those set out in this Agreement.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          9.3 The Licensor warrants it has the right to license the Software and
          Materials to You.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          9.4 The Licensor makes no warranty regarding: (a) the Software&apos;s
          fitness for a particular purpose; (b) the accuracy of any Results; (c)
          uninterrupted operation; and (d) excludes all implied terms to the
          fullest extent permitted by law.
        </Typography>
      </Box>

      {/* 10. Limitation of Liability */}
      <Typography variant="titlemedium" sx={headingStyle}>
        10. Limitation of Liability
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          10.1 Other than in respect of confidentiality breaches, IPR
          infringement, indemnification obligations, or wilful misconduct,
          neither party will be liable for consequential, special or punitive
          damages and each party&apos;s cumulative direct damages will be
          limited to the Licence Fee in the prior 12 month period.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          10.2 To the maximum extent permitted by law You assume all risk as to
          the Software&apos;s use, quality and performance.
        </Typography>
      </Box>

      {/* 11. Improvements */}
      <Typography variant="titlemedium" sx={headingStyle}>
        11. Improvements
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        You must not during the Term attempt to develop any Improvements to the
        Software. In the event that You do make any Improvements, the Licensor
        will own such Improvements and You must assign all rights, procure
        assignment from other authors, and procure moral rights consent.
      </Typography>

      {/* 12. Assignment */}
      <Typography variant="titlemedium" sx={headingStyle}>
        12. Assignment of this Agreement
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        You may not assign this Agreement or any of Your rights or obligations
        created under this Agreement.
      </Typography>

      {/* 13. Term and Termination */}
      <Typography variant="titlemedium" sx={headingStyle}>
        13. Term and Termination
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          13.1 Your licence continues for the period covered by the Licence Fee
          and automatically renews. You may terminate with one month&apos;s
          written notice. If no Licence Fee is required, You may terminate with
          immediate effect.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          13.2 This Licence may also be terminated:
        </Typography>
        <Box component="ol" sx={listStyle}>
          <li>
            <Typography variant="bodysmall">
              By either party on three months written notice;
            </Typography>
          </li>
          <li>
            <Typography variant="bodysmall">
              Immediately by either party if the other is in default and fails
              to remedy within one calendar month of written notice;
            </Typography>
          </li>
          <li>
            <Typography variant="bodysmall">
              Immediately by the Licensor upon: winding up application,
              appointment of receiver, assignment for creditors, institution of
              liquidation proceedings, failure to pay within 14 business days,
              irremediable breach, or written agreement.
            </Typography>
          </li>
        </Box>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          13.3 Upon termination: (a) You are not released from prior
          liabilities; (b) Licence Fees are non-refundable; (c) unpaid fees
          remain due; (d) all rights terminate and You must cease use and
          return/destroy Materials; (e) the Licensor will provide Your Livestock
          Data transfer.
        </Typography>
      </Box>

      {/* 14. Dispute Resolution */}
      <Typography variant="titlemedium" sx={headingStyle}>
        14. Dispute Resolution
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          14.1 Disputes must first be addressed by conferring within 5 Business
          Days, then referred to mediation if not resolved within 15 Business
          Days.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          14.2 A mediator must be appointed within 20 Business Days, failing
          agreement, by the President of the Law Society of New South Wales.
        </Typography>
        <Typography variant="bodysmall" sx={{ mb: 0.5 }}>
          14.3-14.9 Mediation is non-binding unless agreed in writing.
          Confidentiality applies. Neither party may commence court proceedings
          until mediation ends (except for injunctive relief). Costs are shared
          equally. No disparaging remarks may be published until dispute
          processes are exhausted.
        </Typography>
      </Box>

      {/* 15-21. Remaining clauses */}
      <Typography variant="titlemedium" sx={headingStyle}>
        15. Notice
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        Notices may be sent by prepaid registered mail (deemed given 5 working
        days after posting) or by email (deemed given on delivery receipt during
        business hours). The Licensor&apos;s address for notices:
        office@abri.une.edu.au. Your address: the email provided on
        registration.
      </Typography>

      <Typography variant="titlemedium" sx={headingStyle}>
        16. Merger
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        These Terms and Conditions set out the entire agreement between the
        parties and merge all prior discussions and agreements.
      </Typography>

      <Typography variant="titlemedium" sx={headingStyle}>
        17. Waiver
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        Delay in exercising rights is not a waiver. Waiver of any default does
        not constitute a licence to repeat or a waiver of subsequent defaults.
      </Typography>

      <Typography variant="titlemedium" sx={headingStyle}>
        18. Further Assurances
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        Each party shall execute such further documents and perform such further
        acts as may be necessary to carry out all obligations contained in this
        Licence.
      </Typography>

      <Typography variant="titlemedium" sx={headingStyle}>
        19. Entire Agreement
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        This Agreement records the entire agreement between the parties. Neither
        party has given any warranty or representation other than those
        appearing in this Agreement.
      </Typography>

      <Typography variant="titlemedium" sx={headingStyle}>
        20. Force Majeure
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        A party is not liable for a breach caused by circumstances outside their
        direct control, provided they immediately notify the other party and try
        to remedy the cause quickly.
      </Typography>

      <Typography variant="titlemedium" sx={headingStyle}>
        21. Severability
      </Typography>
      <Typography variant="bodysmall" sx={{ mb: 2 }}>
        A term or part of a term that is illegal or unenforceable may be severed
        and the remaining terms continue in force.
      </Typography>
    </Box>
  );
}
